import { KeyStatus } from "../../core/engine/types";
import { IInputManager } from "../../core/engine/types/input-manager";
import { IInputSource, InputActionState } from "./input-source";

export default class TouchInputManager implements IInputManager, IInputSource {
  keyRotate = KeyStatus.NONE;
  keyRotateTwice = KeyStatus.NONE;
  keyDown = KeyStatus.NONE;
  keyRight = KeyStatus.NONE;
  keyLeft = KeyStatus.NONE;
  keyDrop = KeyStatus.NONE;

  private activeButtons = new Set<string>();
  private releasedThisFrame = new Set<string>();
  private enabledFlag = true;

  pressButton(action: 'rotate' | 'rotateTwice' | 'down' | 'right' | 'left' | 'drop') {
    if (!this.enabledFlag) return;
    
    this.activeButtons.add(action);
    const keyName = `key${action.charAt(0).toUpperCase() + action.slice(1)}` as keyof TouchInputManager;
    // Use |= to set PRESSED flag, consistent with keyboard input manager
    (this as any)[keyName] |= KeyStatus.PRESSED;
  }

  releaseButton(action: 'rotate' | 'rotateTwice' | 'down' | 'right' | 'left' | 'drop') {
    if (!this.enabledFlag) return;
    
    this.activeButtons.delete(action);
    const keyName = `key${action.charAt(0).toUpperCase() + action.slice(1)}` as keyof TouchInputManager;
    // Set to RELEASED directly, consistent with keyboard input manager
    (this as any)[keyName] = KeyStatus.RELEASED;
  }

  update() {
    if (!this.enabledFlag) return;

    // Reset released keys that were processed last frame
    this.releasedThisFrame.forEach(action => {
      const keyName = `key${action.charAt(0).toUpperCase() + action.slice(1)}` as keyof TouchInputManager;
      (this as any)[keyName] = KeyStatus.NONE;
    });
    this.releasedThisFrame.clear();

    // Mark newly released keys to be cleared next frame
    const actions: Array<'rotate' | 'rotateTwice' | 'down' | 'right' | 'left' | 'drop'> = 
      ['rotate', 'rotateTwice', 'down', 'right', 'left', 'drop'];
    
    actions.forEach(action => {
      const keyName = `key${action.charAt(0).toUpperCase() + action.slice(1)}` as keyof TouchInputManager;
      const key = this[keyName] as KeyStatus;
      
      if (key === KeyStatus.RELEASED) {
        // Keep RELEASED for this frame, clear it next frame
        this.releasedThisFrame.add(action);
      } else if (key & KeyStatus.PRESSED && !this.activeButtons.has(action)) {
        // Key was pressed but button is no longer active, clear it
        (this as any)[keyName] = KeyStatus.NONE;
      }
    });
  }

  getState(): InputActionState {
    return {
      keyRotate: this.keyRotate,
      keyRotateTwice: this.keyRotateTwice,
      keyDown: this.keyDown,
      keyRight: this.keyRight,
      keyLeft: this.keyLeft,
      keyDrop: this.keyDrop,
    };
  }

  setEnabled(enabled: boolean): void {
    this.enabledFlag = enabled;
    if (!enabled) {
      // Clear all active buttons and reset state when disabled
      this.activeButtons.clear();
      this.releasedThisFrame.clear();
      this.keyRotate = KeyStatus.NONE;
      this.keyRotateTwice = KeyStatus.NONE;
      this.keyDown = KeyStatus.NONE;
      this.keyRight = KeyStatus.NONE;
      this.keyLeft = KeyStatus.NONE;
      this.keyDrop = KeyStatus.NONE;
    }
  }

  isEnabled = (): boolean => {
    return this.enabledFlag;
  };
}

